import { AmazonConnectApp } from "@amazon-connect/app"
import {
  AgentClient,
  ContactClient,
  ContactConnected,
} from "@amazon-connect/contact"
import { VoiceClient } from "@amazon-connect/voice"
import { formatAccountNumber } from "@/helpers/accountHelper"
import { formatUkPhoneToE164 } from "@/helpers/stringHelpers"
import { spaceLogger } from "@/theme/spaceLogger"
import { errorHandler } from "./ErrorService"
import TrackingService from "./TrackingService"

export interface IAwsConnect3pService {
  activate(): Promise<boolean>
  initiateOutboundCall(phoneNumber: string): Promise<boolean>
  isCallInProgress(): boolean
  getAgentState(): string | null
}

interface IContactAttributes {
  validAccountNumber: {
    name: string
    value: string
  }
}

const AWS_CONNECT_ORIGIN = import.meta.env.SO_AWS_CONNECT_ORIGIN
const AWS_CONNECT_AGENT_AVAILABLE_STATE_NAME = "Available"
const ESTABLISH_CONNECTION_WINDOW_MS = 3000

class AwsConnect3pServiceClass implements IAwsConnect3pService {
  private static instance: AwsConnect3pServiceClass | null = null
  private agentClient: AgentClient | null = null
  private contactClient: ContactClient | null = null
  private voiceClient: VoiceClient | null = null
  private currentAgentState: string | null = null
  private callInProgress = false

  public static getInstance() {
    this.instance ??= new AwsConnect3pServiceClass()
    return this.instance
  }

  async activate() {
    const connected = await this.establishConnection()

    if (!connected) {
      return false
    }

    this.log("Connected to AWS connect as 3p App")

    const { provider } = AmazonConnectApp.init({
      onCreate: async () => {
        this.agentClient = new AgentClient({ provider: provider })
        this.contactClient = new ContactClient({ provider: provider })
        this.voiceClient = new VoiceClient({ provider: provider })

        this.agentClient.onStateChanged(agentHandler)
        this.contactClient.onConnected(contactConnectedHandler)
        this.contactClient.onDestroyed(contactEndedHandler)

        await this.setInitialAgentState()

        return Promise.resolve()
      },
      onDestroy: () => {
        return this.handleAppDestroy()
      },
    })

    const agentHandler = async (data) => {
      this.log("Agent State Changed", data)
      this.currentAgentState = data.state || null
    }

    const contactConnectedHandler = async (data: ContactConnected) => {
      this.log("Contact Connected", data)
      this.callInProgress = true
      const contactId = data.contactId

      if (this.contactClient) {
        const contactAttributes = await this.contactClient.getAttributes(
          contactId,
          "*",
        )

        this.log("Contact Attributes", contactAttributes)

        if (this.checkContactAttributes(contactAttributes)) {
          const accountNumberRaw = contactAttributes.validAccountNumber.value

          if (!accountNumberRaw) {
            this.handleError(
              "No account number returned from contact attributes.",
            )
            return
          }

          const accountNumber = formatAccountNumber(accountNumberRaw)
          window.location.href = `/customer/${accountNumber}`
          this.log("Customer Account Redirect", {
            accountNumber: accountNumber,
          })
        } else {
          this.handleError(
            "Unable to retrieve account number from contact attributes.",
          )
        }
      }
    }

    const contactEndedHandler = async (data) => {
      this.log("Contact Ended", data)
      this.callInProgress = false
    }

    return true
  }

  /**
   * Initiates an outbound call to the specified phone number
   * @param phoneNumber - The phone number to call
   * @returns Promise<boolean> - True if call was successfully initiated
   */
  async initiateOutboundCall(phoneNumber: string): Promise<boolean> {
    if (!this.voiceClient) {
      return this.handleError("Voice client not initialized")
    }

    if (!this.isAgentAvailable()) {
      return this.handleError("Agent is not available to make outbound calls")
    }

    if (this.callInProgress) {
      return this.handleError(
        "Cannot initiate outbound call - call already in progress",
      )
    }

    const parsedPhoneNumber = formatUkPhoneToE164(phoneNumber)
    if (!parsedPhoneNumber) {
      return this.handleError(`Invalid phone number format: ${phoneNumber}`)
    }

    try {
      this.log("Initiating Outbound Call", {
        phoneNumber: parsedPhoneNumber,
      })

      const response =
        await this.voiceClient.createOutboundCall(parsedPhoneNumber)

      if (response?.contactId) {
        this.log("Outbound Call Initiated Successfully", {
          contactId: response.contactId,
          phoneNumber: parsedPhoneNumber,
        })
        return true
      } else {
        return this.handleError("Failed to initiate outbound call")
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      this.log("Outbound Call Failed", {
        error: errorMessage,
        phoneNumber: parsedPhoneNumber,
      })
      return this.handleError(`Outbound call failed - ${errorMessage}`)
    }
  }

  /**
   * Checks if there's currently a call in progress
   * @returns boolean
   */
  isCallInProgress(): boolean {
    return this.callInProgress
  }

  /**
   * Gets the current agent state
   * @returns string | null
   */
  getAgentState(): string | null {
    return this.currentAgentState
  }

  private async setInitialAgentState() {
    if (!this.agentClient) {
      this.handleError("Agent client not initialized for state retrieval")
      return
    }

    try {
      const currentState = await this.agentClient.getState()
      this.log("Initial Agent State", { state: currentState.name })
      this.currentAgentState = currentState.name
    } catch (error) {
      this.handleError(`Failed to get initial agent state: ${error}`)
    }
  }

  /**
   * Checks if agent is in a state that allows outbound calls
   * @private
   */
  private isAgentAvailable(): boolean {
    return this.currentAgentState === AWS_CONNECT_AGENT_AVAILABLE_STATE_NAME
  }

  private establishConnection(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (window.self !== window.top) {
        window.parent.postMessage("checkAmazonConnect", AWS_CONNECT_ORIGIN)

        const messageHandler = (event: MessageEvent) => {
          window.removeEventListener("message", messageHandler)
          resolve(event.origin.includes(".my.connect.aws"))
        }

        window.addEventListener("message", messageHandler)

        setTimeout(() => {
          window.removeEventListener("message", messageHandler)
          resolve(false)
        }, ESTABLISH_CONNECTION_WINDOW_MS)
      } else {
        resolve(false)
      }
    })
  }

  private checkContactAttributes(data): data is IContactAttributes {
    return (
      data !== null &&
      typeof data === "object" &&
      typeof data.validAccountNumber === "object" &&
      typeof data.validAccountNumber.value === "string"
    )
  }

  /**
   * Centralized logging method for AWS Connect operations
   * @param name - Log event name
   * @param data - Optional data to log
   * @private
   */
  private log(name: string, data?: object): void {
    const prefix = "[AWS connect event]"

    TrackingService.sendEvent(prefix + " " + name, data)
    spaceLogger([
      { text: prefix, addRocket: true, addNewLine: true },
      { text: `${name}: ` },
      {
        text: data ? " " + JSON.stringify(data) : "",
        isSecondary: true,
        addNewLine: true,
      },
    ])
  }

  /**
   * Centralized error handler for AWS Connect operations
   * @param message - Error message to log and report
   * @returns boolean - Always returns false to indicate failure
   */
  private handleError(message: string): boolean {
    const error = new Error(`AWS Connect: ${message}`)
    errorHandler.handleGenericError(error)
    console.error(error)
    return false
  }

  private handleAppDestroy() {
    this.agentClient = null
    this.contactClient = null
    this.voiceClient = null
    this.currentAgentState = null
    this.callInProgress = false
    return Promise.resolve()
  }
}

export const AwsConnect3pService = AwsConnect3pServiceClass.getInstance()
